import i18n from 'i18next';
import { Dispatch } from 'redux';
import {
  deleteRequest,
  getRequest,
  postRequest,
  putRequest
} from 'src/store/api-handler';
import { SegmentDto, SegmentEditDto } from 'src/store/segments/segment';
import { alertError, alertSuccess } from '../alerts/alerts.actions';
import { requestBegin, requestEnd } from '../requests/requests.actions';

export enum SegmentTypes {
  FetchAll = 'segments/FetchAll',
  Add = 'segments/Add',
  Update = 'segments/Update',
  Delete = 'segments/Delete',
  Failure = 'segments/Failure'
}

export type SegmentAction =
  | { type: SegmentTypes.FetchAll; segments: SegmentDto[] }
  | { type: SegmentTypes.Add; segment: SegmentDto }
  | { type: SegmentTypes.Update; segment: SegmentDto }
  | { type: SegmentTypes.Delete; segment: SegmentDto }
  | { type: SegmentTypes.Failure; errors: any };

export const actionCreators = {
  fetchSegments: (segments: SegmentDto[]): SegmentAction => ({
    type: SegmentTypes.FetchAll,
    segments
  }),
  addSegment: (segment: SegmentDto): SegmentAction => ({
    type: SegmentTypes.Add,
    segment
  }),
  updateSegment: (segment: SegmentDto): SegmentAction => ({
    type: SegmentTypes.Update,
    segment
  }),
  deleteSegment: (segment: SegmentDto): SegmentAction => ({
    type: SegmentTypes.Delete,
    segment
  }),
  segmentRequestFailure: (errors: any): SegmentAction => ({
    type: SegmentTypes.Failure,
    errors
  })
};

export const fetchSegments = () => {
  return async (dispatch: Dispatch) => {
    dispatch(requestBegin(SegmentTypes.FetchAll));
    const { ok, data, error } = await getRequest<SegmentDto[]>('/segments');
    dispatch(requestEnd(SegmentTypes.FetchAll));
    return ok
      ? dispatch(actionCreators.fetchSegments(data))
      : dispatch(actionCreators.segmentRequestFailure(error));
  };
};

export const createSegment = (segment: SegmentEditDto) => {
  return async (dispatch: Dispatch) => {
    const { ok, error } = await postRequest('/segments', segment);

    if (ok) {
      return dispatch(alertSuccess(i18n.t('segment.created')));
    }

    dispatch(alertError(error?.errors?.detail ?? i18n.t('create.failed')));
    return dispatch(actionCreators.segmentRequestFailure(error));
  };
};

export const editSegment = (id: number, segment: SegmentEditDto) => {
  return async (dispatch: Dispatch) => {
    const { ok, error } = await putRequest(`/segments/${id}`, segment);

    if (ok) {
      return dispatch(alertSuccess(i18n.t('segment.updated')));
    }

    dispatch(alertError(error?.errors?.detail ?? i18n.t('save.failed')));
    return dispatch(actionCreators.segmentRequestFailure(error));
  };
};

export const deleteSegment = (id: number, segment: SegmentDto) => {
  return async (dispatch: Dispatch) => {
    const { ok, error } = await deleteRequest(`/segments/${id}`, segment);

    if (ok) {
      dispatch(alertSuccess(i18n.t('segment.deleted')));
      return dispatch(actionCreators.deleteSegment(segment));
    }

    dispatch(alertError(error?.errors?.detail ?? i18n.t('remove.failed')));
    return dispatch(actionCreators.segmentRequestFailure(error));
  };
};
